// Styling extracted from the former HeroSearchForm.module.css.
// locationFormSx is a normal sx for the owned <form> (now <Box component="form">).
// The remaining classes are forwarded as className strings into shared children
// (ControlledLocationAutocomplete, BookingDateRangeFilter), so they are reproduced as
// emotion <GlobalStyles> with plain, page-prefixed class names — the className routing
// is unchanged, so the rendered result is pixel-identical, and GlobalStyles are inserted
// as base styles so any competing MUI styles on those elements still win as before.
import GlobalStyles from '@mui/material/GlobalStyles';
import React from 'react';

export const HERO_DESKTOP_INPUT_ROOT = 'heroSearchFormDesktopInputRoot';
export const HERO_DESKTOP_INPUT = 'heroSearchFormDesktopInput';
export const HERO_DATE_RANGE_FILTER = 'heroSearchFormDateRangeFilter';
export const HERO_DATE_RANGE_FILTER_POPUP = 'heroSearchFormDateRangeFilterPopup';
export const HERO_DATE_RANGE_FILTER_BUTTON = 'heroSearchFormDateRangeFilterButton';
export const HERO_DATE_RANGE_FILTER_BUTTON_WITH_VALUES =
  'heroSearchFormDateRangeFilterButtonWithValues';

// former .locationForm (applied to the owned <form>)
export const locationFormSx = (theme) => ({
  width: '100%',
  borderRadius: '50px',
  '&:hover': {
    backgroundColor: theme.tokens.matterColorNegative,
  },
  '&:focus-within, &:active': {
    outline: 'none',
    backgroundColor: theme.tokens.matterColorLight,
    borderColor: 'transparent',
    textDecoration: 'none',
    boxShadow: theme.tokens.boxShadowFilterButton,
  },
  [theme.breakpoints.up('sm')]: {
    flex: 2,
  },
  [theme.breakpoints.up('md')]: {
    borderBottomLeftRadius: '50px',
    borderTopLeftRadius: '50px',
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
  },
});

// Shared base for the two date-range-filter button states; they differ only in base color
// (.dateRangeFilterButton = matterColorAnti, .dateRangeFilterButtonWithValues = matterColor).
const dateRangeButtonBaseSx = (theme) => ({
  ...theme.mixins.marketplaceSearchFilterLabelFontStyles,
  padding: '9px 16px 10px 16px',
  width: '100%',
  height: '72px',
  minHeight: 0,
  border: 0,
  '&:hover': {
    backgroundColor: theme.tokens.matterColorNegative,
    color: theme.tokens.matterColor,
  },
  '&:focus': {
    outline: 'none',
    backgroundColor: theme.tokens.matterColorLight,
    borderColor: 'transparent',
    textDecoration: 'none',
    boxShadow: theme.tokens.boxShadowFilterButton,
  },
});

export const HeroSearchFormGlobalStyles = () => (
  <GlobalStyles
    styles={(theme) => ({
      // former .desktopInputRoot
      [`.${HERO_DESKTOP_INPUT_ROOT}`]: {
        height: theme.tokens.topbarHeightDesktop,
        paddingLeft: '8px',
      },
      // former .desktopInput (@apply --marketplaceSearchFilterLabelFontStyles + own md override)
      [`.${HERO_DESKTOP_INPUT}`]: {
        flexGrow: 1,
        ...theme.mixins.marketplaceSearchFilterLabelFontStyles,
        height: theme.tokens.topbarHeightDesktop,
        lineHeight: 'unset',
        border: 'none',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: '10px',
        textOverflow: 'ellipsis',
        overflowX: 'hidden',
        '&::-webkit-input-placeholder': {
          ...theme.mixins.marketplaceSearchFilterLabelFontStyles,
          lineHeight: 'normal',
        },
        '&:hover, &:focus': {
          borderBottomColor: theme.tokens.marketplaceColor,
          outline: 'none',
        },
        '&:hover::placeholder, &:focus::placeholder': {
          color: theme.tokens.matterColor,
        },
        '&::placeholder': {
          ...theme.mixins.marketplaceSearchFilterLabelFontStyles,
          textOverflow: 'ellipsis',
          overflowX: 'hidden',
          transition: theme.tokens.transitionStyleButton,
        },
        marginTop: 0,
        marginBottom: 0,
        // own md block MERGED with the mixin's md sub-block (md-key-collision rule);
        // own fontWeight (semiBold) overrides the mixin md fontWeight (medium).
        [theme.breakpoints.up('md')]: {
          ...theme.mixins.marketplaceSearchFilterLabelFontStyles['@media (min-width:900px)'],
          marginTop: 0,
          marginBottom: 0,
          fontWeight: theme.tokens.fontWeightSemiBold,
        },
      },
      // former .dateRangeFilter
      [`.${HERO_DATE_RANGE_FILTER}`]: {
        display: 'none',
        [theme.breakpoints.up('md')]: {
          display: 'block',
        },
      },
      // former .dateRangeFilterPopup
      [`.${HERO_DATE_RANGE_FILTER_POPUP}`]: {
        display: 'block',
        visibility: 'hidden',
        opacity: 0,
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: theme.tokens.zIndexPopup,
        left: 'unset !important',
        minWidth: '300px',
        marginTop: '5px',
        backgroundColor: theme.tokens.matterColorLight,
        borderTop: `1px solid ${theme.tokens.matterColorNegative}`,
        boxShadow: theme.tokens.boxShadowPopup,
        borderRadius: '4px',
        transition: theme.tokens.transitionStyleButton,
      },
      // former .dateRangeFilterButton
      [`.${HERO_DATE_RANGE_FILTER_BUTTON}`]: {
        ...dateRangeButtonBaseSx(theme),
        color: theme.tokens.matterColorAnti,
      },
      // former .dateRangeFilterButtonWithValues (identical to button except base color)
      [`.${HERO_DATE_RANGE_FILTER_BUTTON_WITH_VALUES}`]: {
        ...dateRangeButtonBaseSx(theme),
        color: theme.tokens.matterColor,
      },
    })}
  />
);
